/**
 * Candidate Experience Drawer (STN client round 2026-08) — the full-profile
 * view over one search result: complete WHY THEY FIT, THINGS TO LOOK INTO
 * (which lives ONLY here since the card rebuild), and the WORK EXPERIENCE
 * timeline from MAPS.PersonJob. Mounted by the search page per the
 * maps-drawer contract (*ngIf + (closed)).
 */
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import type { MapsCandidate, MapsContactCandidateStatus, MapsEmploymentRole } from '../../models/maps.types';
import { FormatRoleMeta } from '../../utils/recent-roles';
import { FormatContactStatusChip, IsContactStatusFailure } from '../../utils/contact-status';

interface TimelineRow {
    title: string | null;
    meta: string | null;
    last: boolean;
}

@Component({
    selector: 'maps-experience-drawer',
    standalone: false,
    templateUrl: './experience-drawer.component.html',
    styleUrl: './experience-drawer.component.scss',
})
export class ExperienceDrawerComponent implements OnChanges {
    @Input({ required: true }) candidate!: MapsCandidate;
    /** Contact rollup for this candidate (null = never contacted). */
    @Input() contactStatus: MapsContactCandidateStatus | null = null;
    /** Passed through to the summary boxes' retry affordance. */
    @Input() summariesFailed = false;
    @Output() closed = new EventEmitter<void>();
    @Output() summariesRetry = new EventEmitter<void>();
    /** Footer Contact — the search page opens the contact modal above this drawer. */
    @Output() contact = new EventEmitter<void>();

    timeline: TimelineRow[] = [];
    /** Pre-formatted chip text (NG0100: never a method call in the binding). */
    contactStatusText: string | null = null;
    contactStatusIsFailure = false;

    ngOnChanges(): void {
        const roles: MapsEmploymentRole[] = this.candidate.employmentHistory ?? [];
        this.timeline = roles.map((r, ix) => ({
            title: r.jobTitle?.trim() || null,
            meta: FormatRoleMeta(r),
            last: ix === roles.length - 1,
        }));
        this.contactStatusText = this.contactStatus !== null ? FormatContactStatusChip(this.contactStatus) : null;
        this.contactStatusIsFailure = this.contactStatus !== null && IsContactStatusFailure(this.contactStatus.lastStatus);
    }

    get displayName(): string {
        return `${this.candidate.firstName} ${this.candidate.lastName}`.trim();
    }

    get roleLine(): string | null {
        const parts = [this.candidate.jobTitle, this.candidate.organization].filter((p): p is string => !!p);
        return parts.length > 0 ? parts.join(', ') : null;
    }

    get locationText(): string | null {
        const loc = this.candidate.location;
        if (!loc) return null;
        const parts = [loc.city, loc.state, loc.country].filter((p): p is string => !!p);
        return parts.length > 0 ? parts.join(', ') : null;
    }

    get experienceHeading(): string {
        const n = this.timeline.length;
        if (n === 0) return 'Work Experience';
        return `Work Experience · ${n} ${n === 1 ? 'role' : 'roles'}`;
    }

    /** Gates the PROFILES section — mirrors the shared maps-profile-links
     *  row's own *ngIf so an empty header never renders. */
    get hasProfileLinks(): boolean {
        return !!(this.candidate.linkedInUrl || this.candidate.aguProfileUrl || this.candidate.orcid?.trim());
    }

}
